import { useEffect, useState } from "react";
import { fetchDashboardSummary, type DashboardSummary } from "../services/dashboard";

interface HospitalDashboardData {
    data: DashboardSummary | null;
    loading: boolean;
    error: string | null;
    lastUpdated: string | null;
    refresh: () => void;
}

export function useHospitalDashboard(pollMs = 10000, hospitalId?: string): HospitalDashboardData {
    const [data, setData] = useState<DashboardSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastUpdated, setLastUpdated] = useState<string | null>(null);

    const load = async (showSpinner: boolean) => {
        if (showSpinner) setLoading(true);
        try {
            const result = await fetchDashboardSummary(hospitalId);
            setData(result);
            setError(null);
            setLastUpdated(new Date().toISOString());
        } catch (err: any) {
            console.error("Failed to fetch hospital dashboard", err);
            setError(err.message || "Unable to load dashboard data");
        } finally {
            if (showSpinner) setLoading(false);
        }
    };

    useEffect(() => {
        void load(true);
        const interval = window.setInterval(() => {
            void load(false);
        }, pollMs);

        return () => window.clearInterval(interval);
    }, [pollMs, hospitalId]);

    return {
        data,
        loading,
        error,
        lastUpdated,
        refresh: () => void load(true)
    };
}
